// src/features/invoices/components/PaymentReconciliationView.tsx
//
// UI-Komponente fuer den Zahlungsabgleich:
//   - Upload-Bereich oben (CAMT.052-ZIP)
//   - Letzte Import-Runs (Audit-Trail-Kurzform)
//   - Tab-Switcher: Offen / Bezahlt
//   - Offen-Liste:  Actions je nach bisheriger Mahnstufe (Ablauf 1 -> 3 -> 2):
//                     * keine Mahnung verschickt   -> [Erinnerung senden] [Als bezahlt markieren]
//                     * Level 1 verschickt          -> [SEPA-Mahnung senden] [Als bezahlt markieren]
//                     * Level 3 verschickt          -> [Letzte Erinnerung senden] [Als bezahlt markieren]
//                     * Level 2 verschickt          -> [Als bezahlt markieren]
//     (Der SEPA-Mahnung-Button entfaellt bei Kunden, die bereits per Lastschrift zahlen.)
//   - Bezahlt-Liste: Action "Als unbezahlt markieren"
//   - Mahnvorschläge: werden beim Öffnen und nach jedem Import auf dem aktuellen
//     Stand berechnet (seit 04.11.2026, vorher nur nach einem Import)
//   - Bankverbindung (EBICS): nur in der Ansicht von Tim Wolter, siehe EbicsBankverbindung

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Upload, Check, X, RotateCcw, FileText, Loader, AlertTriangle, Calendar,
  Banknote, Hand, Mail, AlertCircle, CreditCard
} from 'lucide-react';
import { useAuth } from '../../auth';
import {
  paymentReconciliationService,
  OpenInvoiceListItem,
  PaidInvoiceListItem,
  BankImportRun,
  ImportCamtResult
} from '../../../services/payment/paymentReconciliationService';
import {
  paymentReminderService,
  ReminderProposals,
  ReminderProposalItem
} from '../../../services/payment/paymentReminderService';
import { EbicsBankverbindung } from './EbicsBankverbindung';

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '–';
  return new Date(dateStr).toLocaleDateString('de-DE');
};

const formatDateTime = (dateStr: string): string => {
  return new Date(dateStr).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const todayIso = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const daysSince = (dateStr: string | null): number | null => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const today = new Date();
  d.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return Math.floor((today.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
};

export const PaymentReconciliationView: React.FC = () => {
  const { user } = useAuth();

  // Bereich Bankverbindung (EBICS) nur in der Ansicht von Tim Wolter
  const istTimWolter = user?.email === 'tim.wolter@xtax.de';

  // Tab-State innerhalb des Zahlungsabgleichs
  const [activeTab, setActiveTab] = useState<'offen' | 'bezahlt'>('offen');

  // Listen
  const [openInvoices, setOpenInvoices] = useState<OpenInvoiceListItem[]>([]);
  const [paidInvoices, setPaidInvoices] = useState<PaidInvoiceListItem[]>([]);
  const [importRuns, setImportRuns] = useState<BankImportRun[]>([]);
  const [loadingLists, setLoadingLists] = useState(false);

  // Upload-State
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadResult, setUploadResult] = useState<ImportCamtResult | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Inline-Markierung "Als bezahlt"
  const [markPaidInvoiceId, setMarkPaidInvoiceId] = useState<string | null>(null);
  const [markPaidDatum, setMarkPaidDatum] = useState<string>(todayIso());
  const [markPaidBusy, setMarkPaidBusy] = useState<string | null>(null);

  // "Als unbezahlt markieren" Busy-State
  const [markUnpaidBusy, setMarkUnpaidBusy] = useState<string | null>(null);

  // "Mahnung senden" Busy-State (Feature Zahlungserinnerung / SEPA-Mahnung)
  const [reminderBusy, setReminderBusy] = useState<string | null>(null);

  // Mahnvorschlaege (Feature halbautomatische Mahnvorschlaege)
  const [proposals, setProposals] = useState<ReminderProposals | null>(null);
  const [proposalsLoading, setProposalsLoading] = useState(false);
  const [selectedL1, setSelectedL1] = useState<Set<string>>(new Set());
  const [selectedL3, setSelectedL3] = useState<Set<string>>(new Set());
  const [selectedL2, setSelectedL2] = useState<Set<string>>(new Set());
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchResult, setBatchResult] = useState<{ sent: number; failed: number; errors: string[] } | null>(null);

  // ============================================
  // DATEN LADEN
  // ============================================
  const loadAll = useCallback(async () => {
    setLoadingLists(true);
    try {
      const [open, paid, runs] = await Promise.all([
        paymentReconciliationService.getOpenInvoices(),
        paymentReconciliationService.getPaidInvoices(),
        paymentReconciliationService.getRecentImportRuns(5)
      ]);
      setOpenInvoices(open);
      setPaidInvoices(paid);
      setImportRuns(runs);
    } finally {
      setLoadingLists(false);
    }
  }, []);

  const loadProposals = useCallback(async () => {
    setProposalsLoading(true);
    try {
      const p = await paymentReminderService.getReminderProposals();
      setProposals(p);
      setSelectedL1(new Set(p.level1.map(i => i.id)));
      setSelectedL3(new Set(p.level3.map(i => i.id)));
      setSelectedL2(new Set(p.level2.map(i => i.id)));
    } finally {
      setProposalsLoading(false);
    }
  }, []);

  // Listen und Mahnvorschläge beim Öffnen laden. Die Vorschläge stehen damit
  // auch ohne vorherigen Upload auf dem aktuellen Stand zur Verfügung.
  useEffect(() => {
    loadAll();
    loadProposals();
  }, [loadAll, loadProposals]);

  // ============================================
  // UPLOAD-HANDLER
  // ============================================
  const handleFileSelected = async (file: File) => {
    if (!user?.email) {
      setUploadError('Kein angemeldeter Benutzer – Upload nicht moeglich.');
      return;
    }

    const lowerName = file.name.toLowerCase();
    if (!lowerName.endsWith('.zip')) {
      setUploadError('Bitte eine ZIP-Datei mit CAMT.052-XML-Dateien auswaehlen.');
      return;
    }

    setUploadBusy(true);
    setUploadResult(null);
    setUploadError(null);
    setProposals(null);
    setBatchResult(null);

    try {
      const result = await paymentReconciliationService.uploadCamtZip(file, user.email);
      if (!result.success) {
        setUploadError(result.error || 'Import fehlgeschlagen');
      } else {
        setUploadResult(result);
        // Listen neu laden, damit neu bezahlte Rechnungen vom "Offen"- in den "Bezahlt"-Tab wandern
        await loadAll();
        // Mahnvorschlaege auf Basis des Nach-Import-Stands berechnen (Feature halbautomatische Mahnvorschlaege)
        await loadProposals();
      }
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Unbekannter Fehler');
    } finally {
      setUploadBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ============================================
  // MARKIERUNG-HANDLER
  // ============================================
  const startMarkPaid = (invoiceId: string) => {
    setMarkPaidInvoiceId(invoiceId);
    setMarkPaidDatum(todayIso());
  };

  const cancelMarkPaid = () => {
    setMarkPaidInvoiceId(null);
  };

  const confirmMarkPaid = async (inv: OpenInvoiceListItem) => {
    if (!markPaidDatum) return;
    setMarkPaidBusy(inv.id);
    try {
      const result = await paymentReconciliationService.markInvoicePaidManually(
        inv.id,
        markPaidDatum,
        inv.brutto_betrag
      );
      if (!result.success) {
        alert(`Fehler: ${result.error}`);
      } else {
        setMarkPaidInvoiceId(null);
        await loadAll();
      }
    } finally {
      setMarkPaidBusy(null);
    }
  };

  const handleMarkUnpaid = async (inv: PaidInvoiceListItem) => {
    if (!window.confirm(`Rechnung ${inv.rechnungsnummer} (${formatCurrency(inv.brutto_betrag)}) wirklich als unbezahlt markieren?`)) return;
    setMarkUnpaidBusy(inv.id);
    try {
      const result = await paymentReconciliationService.markInvoiceUnpaid(inv.id);
      if (!result.success) {
        alert(`Fehler: ${result.error}`);
      } else {
        await loadAll();
      }
    } finally {
      setMarkUnpaidBusy(null);
    }
  };

  // ============================================
  // MAHNUNG VERSENDEN (Zahlungserinnerung / SEPA-Mahnung / letzte Erinnerung)
  // ============================================
  const handleSendReminder = async (inv: OpenInvoiceListItem, level: 1 | 2 | 3) => {
    if (!user?.email) {
      alert('Kein angemeldeter Benutzer – Versand nicht möglich.');
      return;
    }

    let confirmText: string;
    if (level === 2) {
      const gemahntAm = formatDate(inv.last_reminder_sent_at);
      confirmText = `Letzte Erinnerung für Rechnung ${inv.rechnungsnummer} (${formatCurrency(inv.brutto_betrag)}) jetzt versenden?\n\nDer Kunde wurde zuletzt am ${gemahntAm} gemahnt. Die letzte Erinnerung setzt eine Frist von 7 Tagen und kann nur einmal verschickt werden.`;
    } else if (level === 3) {
      confirmText = `SEPA-Mahnung für Rechnung ${inv.rechnungsnummer} (${formatCurrency(inv.brutto_betrag)}) jetzt versenden?\n\nDer Kunde erhält zusätzlich das Angebot, per Klick bequem auf SEPA-Lastschrift umzustellen. Die SEPA-Mahnung kann nur einmal verschickt werden.`;
    } else {
      confirmText = `Zahlungserinnerung für Rechnung ${inv.rechnungsnummer} (${formatCurrency(inv.brutto_betrag)}) jetzt versenden?`;
    }

    if (!window.confirm(confirmText)) return;

    setReminderBusy(inv.id);
    try {
      const result = await paymentReminderService.sendPaymentReminder(inv.id, user.email, level);
      if (!result.success) {
        alert(`Fehler: ${result.error}`);
      } else {
        const label = level === 2 ? 'Letzte Erinnerung' : level === 3 ? 'SEPA-Mahnung' : 'Zahlungserinnerung';
        alert(`${label} versendet an ${result.sentTo}.`);
        await loadAll();
      }
    } finally {
      setReminderBusy(null);
    }
  };

  // ============================================
  // MAHNVORSCHLAEGE: AUSWAHL + BATCH-VERSAND (Feature halbautomatische Mahnvorschlaege)
  // ============================================
  const toggleL1 = (id: string) => {
    setSelectedL1(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleL3 = (id: string) => {
    setSelectedL3(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleL2 = (id: string) => {
    setSelectedL2(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBatchSend = async () => {
    if (!user?.email) {
      alert('Kein angemeldeter Benutzer – Versand nicht möglich.');
      return;
    }
    if (!proposals) return;

    const l1Items = proposals.level1.filter(i => selectedL1.has(i.id));
    const l3Items = proposals.level3.filter(i => selectedL3.has(i.id));
    const l2Items = proposals.level2.filter(i => selectedL2.has(i.id));
    const total = l1Items.length + l3Items.length + l2Items.length;
    if (total === 0) {
      alert('Keine Mahnungen ausgewählt.');
      return;
    }

    if (!window.confirm(`${total} Mahnung(en) jetzt versenden?\n\nZahlungserinnerungen: ${l1Items.length}\nSEPA-Mahnungen: ${l3Items.length}\nLetzte Erinnerungen: ${l2Items.length}`)) return;

    setBatchBusy(true);
    setBatchResult(null);

    let sent = 0;
    const errors: string[] = [];

    // Sequenziell versenden (Gmail-Limits, kein Auto-Retry).
    // Reihenfolge entlang des Ablaufs: erst Level 1, dann Level 3, dann Level 2.
    for (const item of l1Items) {
      const result = await paymentReminderService.sendPaymentReminder(item.id, user.email, 1);
      if (result.success) sent++;
      else errors.push(`${item.rechnungsnummer}: ${result.error || 'Fehler'}`);
    }
    for (const item of l3Items) {
      const result = await paymentReminderService.sendPaymentReminder(item.id, user.email, 3);
      if (result.success) sent++;
      else errors.push(`${item.rechnungsnummer}: ${result.error || 'Fehler'}`);
    }
    for (const item of l2Items) {
      const result = await paymentReminderService.sendPaymentReminder(item.id, user.email, 2);
      if (result.success) sent++;
      else errors.push(`${item.rechnungsnummer}: ${result.error || 'Fehler'}`);
    }

    setBatchBusy(false);
    setBatchResult({ sent, failed: errors.length, errors });

    // Listen + Vorschlaege neu laden — versendete Rechnungen fallen aus den Vorschlaegen
    await loadAll();
    await loadProposals();
  };

  // ============================================
  // COUNTS
  // ============================================
  const openSum = openInvoices.reduce((sum, i) => sum + i.brutto_betrag, 0);
  const selectedTotal = selectedL1.size + selectedL3.size + selectedL2.size;

  // ============================================
  // RENDER
  // ============================================
  return (
    <div>
      {/* ====================================== */}
      {/* BANKVERBINDUNG (EBICS), nur Tim Wolter  */}
      {/* ====================================== */}
      {istTimWolter && <EbicsBankverbindung />}

      {/* ====================================== */}
      {/* UPLOAD-BEREICH                          */}
      {/* ====================================== */}
      <div style={{
        backgroundColor: 'white', borderRadius: '8px', border: '1px solid #E5E7EB',
        padding: '20px', marginBottom: '20px'
      }}>
        <div style={{ fontSize: '15px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
          Bankauszug importieren
        </div>
        <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '16px', lineHeight: '1.5' }}>
          ZIP-Datei mit CAMT.052-XML-Dateien aus dem Online-Banking. Zahlungseingaenge, bei denen
          Rechnungsnummer und Bruttobetrag exakt zu einer offenen Rechnung passen, werden automatisch
          als bezahlt markiert.
        </div>

        <div
          onClick={() => !uploadBusy && fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); if (!uploadBusy) (e.currentTarget as HTMLDivElement).style.borderColor = '#3B82F6'; }}
          onDragLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = '#D1D5DB'; }}
          onDrop={(e) => {
            e.preventDefault();
            (e.currentTarget as HTMLDivElement).style.borderColor = '#D1D5DB';
            if (uploadBusy) return;
            const file = e.dataTransfer.files[0];
            if (file) handleFileSelected(file);
          }}
          style={{
            border: '2px dashed #D1D5DB', borderRadius: '8px', padding: '32px 24px',
            textAlign: 'center', cursor: uploadBusy ? 'not-allowed' : 'pointer',
            transition: 'border-color 0.2s', opacity: uploadBusy ? 0.6 : 1
          }}
        >
          {uploadBusy ? (
            <>
              <Loader size={32} color="#3B82F6" style={{ margin: '0 auto 12px', animation: 'spin 1s linear infinite' }} />
              <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                ZIP wird verarbeitet ...
              </div>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </>
          ) : (
            <>
              <Upload size={32} color="#9CA3AF" style={{ margin: '0 auto 12px' }} />
              <div style={{ fontSize: '15px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                CAMT.052-ZIP auswaehlen
              </div>
              <div style={{ fontSize: '13px', color: '#6B7280' }}>
                ZIP-Datei hierhin ziehen oder klicken
              </div>
            </>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".zip,.ZIP,application/zip,application/x-zip-compressed"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileSelected(file);
          }}
        />

        {/* Ergebnis */}
        {uploadResult && (
          <div style={{
            marginTop: '16px', padding: '12px 16px', backgroundColor: '#ECFDF5',
            borderRadius: '6px', border: '1px solid #A7F3D0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <Check size={18} color="#059669" />
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#059669' }}>
                Import abgeschlossen
              </span>
            </div>
            <div style={{ fontSize: '13px', color: '#065F46', lineHeight: '1.6' }}>
              <strong>{uploadResult.auto_matched_count}</strong> von <strong>{uploadResult.crdt_entries}</strong> Zahlungseingaengen automatisch zugeordnet.
              {' '}({uploadResult.total_entries} Buchungen insgesamt, Zeitraum {formatDate(uploadResult.period_from || null)} – {formatDate(uploadResult.period_to || null)})
              {uploadResult.parse_errors && uploadResult.parse_errors.length > 0 && (
                <div style={{ marginTop: '6px', color: '#92400E' }}>
                  Hinweis: {uploadResult.parse_errors.length} XML-Datei(en) konnten nicht gelesen werden.
                </div>
              )}
            </div>
          </div>
        )}

        {uploadError && (
          <div style={{
            marginTop: '16px', padding: '12px 16px', backgroundColor: '#FEF2F2',
            borderRadius: '6px', border: '1px solid #FECACA'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <X size={18} color="#DC2626" />
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#DC2626' }}>Fehler beim Import</span>
            </div>
            <div style={{ fontSize: '13px', color: '#991B1B' }}>{uploadError}</div>
          </div>
        )}

        {/* Import-Historie */}
        {importRuns.length > 0 && (
          <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #E5E7EB' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', marginBottom: '8px', textTransform: 'uppercase' }}>
              Letzte Importe
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {importRuns.map(run => (
                <div key={run.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  fontSize: '13px', color: '#374151', padding: '6px 10px',
                  backgroundColor: '#F9FAFB', borderRadius: '4px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                    <FileText size={14} color="#6B7280" />
                    <span style={{ fontWeight: '500' }}>{formatDateTime(run.imported_at)}</span>
                    <span style={{ color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {run.filename}
                    </span>
                  </div>
                  <div style={{ color: '#6B7280', whiteSpace: 'nowrap', marginLeft: '12px' }}>
                    <strong style={{ color: '#059669' }}>{run.auto_matched_count}</strong> / {run.crdt_entries} zugeordnet
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ====================================== */}
      {/* MAHNVORSCHLAEGE (halbautomatisch)       */}
      {/* ====================================== */}
      {proposals !== null && (
        <div style={{
          backgroundColor: 'white', borderRadius: '8px', border: '1px solid #E5E7EB',
          padding: '20px', marginBottom: '20px'
        }}>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <div style={{ fontSize: '15px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
            Mahnvorschlaege
          </div>
          <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '16px', lineHeight: '1.5' }}>
            Auf Basis des aktuellen Stands. Zahlungserinnerung ab 7 Tagen ueberfaellig (noch nicht gemahnt),
            SEPA-Mahnung ab 14 Tagen ueberfaellig und 7 Tagen nach der Zahlungserinnerung, letzte Erinnerung
            7 Tage nach der SEPA-Mahnung. Auswahl anpassen und gesammelt versenden.
          </div>

          {proposalsLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#6B7280', fontSize: '14px' }}>
              <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
              Vorschlaege werden berechnet ...
            </div>
          ) : (proposals.level1.length === 0 && proposals.level3.length === 0 && proposals.level2.length === 0) ? (
            <div style={{ fontSize: '14px', color: '#6B7280' }}>
              Keine faelligen Mahnvorschlaege.
            </div>
          ) : (
            <>
              {/* Level 1 */}
              {proposals.level1.length > 0 && (
                <div style={{ marginBottom: (proposals.level3.length > 0 || proposals.level2.length > 0) ? '20px' : '0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="checkbox"
                      checked={selectedL1.size === proposals.level1.length}
                      onChange={(e) => setSelectedL1(e.target.checked ? new Set(proposals.level1.map(i => i.id)) : new Set<string>())}
                      disabled={batchBusy}
                    />
                    <Mail size={15} color="#2563EB" />
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Zahlungserinnerung</span>
                    <span style={{
                      backgroundColor: '#BFDBFE', color: '#1E3A8A',
                      padding: '1px 6px', borderRadius: '10px', fontSize: '11px', fontWeight: '600'
                    }}>
                      {selectedL1.size} / {proposals.level1.length}
                    </span>
                  </div>
                  <div style={{ border: '1px solid #E5E7EB', borderRadius: '6px', overflow: 'hidden' }}>
                    {proposals.level1.map((item: ReminderProposalItem) => (
                      <label key={item.id} style={{
                        display: 'grid', gridTemplateColumns: '28px 120px 1fr 130px 150px',
                        gap: '12px', padding: '10px 14px', borderBottom: '1px solid #F3F4F6',
                        fontSize: '14px', color: '#111827', alignItems: 'center',
                        cursor: batchBusy ? 'not-allowed' : 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          checked={selectedL1.has(item.id)}
                          onChange={() => toggleL1(item.id)}
                          disabled={batchBusy}
                        />
                        <span style={{ fontWeight: '500' }}>{item.rechnungsnummer}</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.empfaenger_firma}</span>
                        <span style={{ textAlign: 'right', fontWeight: '500' }}>{formatCurrency(item.brutto_betrag)}</span>
                        <span style={{ textAlign: 'right', color: item.tage_ueberfaellig > 30 ? '#DC2626' : '#D97706', fontWeight: '500' }}>
                          {item.tage_ueberfaellig} Tage ueberfaellig
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Level 3 (SEPA-Mahnung) */}
              {proposals.level3.length > 0 && (
                <div style={{ marginBottom: proposals.level2.length > 0 ? '20px' : '0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="checkbox"
                      checked={selectedL3.size === proposals.level3.length}
                      onChange={(e) => setSelectedL3(e.target.checked ? new Set(proposals.level3.map(i => i.id)) : new Set<string>())}
                      disabled={batchBusy}
                    />
                    <CreditCard size={15} color="#4338CA" />
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>SEPA-Mahnung</span>
                    <span style={{
                      backgroundColor: '#C7D2FE', color: '#3730A3',
                      padding: '1px 6px', borderRadius: '10px', fontSize: '11px', fontWeight: '600'
                    }}>
                      {selectedL3.size} / {proposals.level3.length}
                    </span>
                  </div>
                  <div style={{ border: '1px solid #C7D2FE', borderRadius: '6px', overflow: 'hidden' }}>
                    {proposals.level3.map((item: ReminderProposalItem) => (
                      <label key={item.id} style={{
                        display: 'grid', gridTemplateColumns: '28px 120px 1fr 130px 150px 160px',
                        gap: '12px', padding: '10px 14px', borderBottom: '1px solid #E0E7FF',
                        fontSize: '14px', color: '#111827', alignItems: 'center',
                        cursor: batchBusy ? 'not-allowed' : 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          checked={selectedL3.has(item.id)}
                          onChange={() => toggleL3(item.id)}
                          disabled={batchBusy}
                        />
                        <span style={{ fontWeight: '500' }}>{item.rechnungsnummer}</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.empfaenger_firma}</span>
                        <span style={{ textAlign: 'right', fontWeight: '500' }}>{formatCurrency(item.brutto_betrag)}</span>
                        <span style={{ textAlign: 'right', color: item.tage_ueberfaellig > 30 ? '#DC2626' : '#D97706', fontWeight: '500' }}>
                          {item.tage_ueberfaellig} Tage ueberfaellig
                        </span>
                        <span style={{ textAlign: 'right', fontSize: '11px', color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                          1. Erinnerung: {formatDate(item.erste_erinnerung_am)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Level 2 (Letzte Erinnerung) */}
              {proposals.level2.length > 0 && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="checkbox"
                      checked={selectedL2.size === proposals.level2.length}
                      onChange={(e) => setSelectedL2(e.target.checked ? new Set(proposals.level2.map(i => i.id)) : new Set<string>())}
                      disabled={batchBusy}
                    />
                    <AlertCircle size={15} color="#92400E" />
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Letzte Erinnerung</span>
                    <span style={{
                      backgroundColor: '#FCD34D', color: '#78350F',
                      padding: '1px 6px', borderRadius: '10px', fontSize: '11px', fontWeight: '600'
                    }}>
                      {selectedL2.size} / {proposals.level2.length}
                    </span>
                  </div>
                  <div style={{ border: '1px solid #FCD34D', borderRadius: '6px', overflow: 'hidden' }}>
                    {proposals.level2.map((item: ReminderProposalItem) => (
                      <label key={item.id} style={{
                        display: 'grid', gridTemplateColumns: '28px 120px 1fr 130px 150px 160px',
                        gap: '12px', padding: '10px 14px', borderBottom: '1px solid #FEF3C7',
                        fontSize: '14px', color: '#111827', alignItems: 'center',
                        cursor: batchBusy ? 'not-allowed' : 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          checked={selectedL2.has(item.id)}
                          onChange={() => toggleL2(item.id)}
                          disabled={batchBusy}
                        />
                        <span style={{ fontWeight: '500' }}>{item.rechnungsnummer}</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.empfaenger_firma}</span>
                        <span style={{ textAlign: 'right', fontWeight: '500' }}>{formatCurrency(item.brutto_betrag)}</span>
                        <span style={{ textAlign: 'right', color: '#DC2626', fontWeight: '500' }}>
                          {item.tage_ueberfaellig} Tage ueberfaellig
                        </span>
                        <span style={{ textAlign: 'right', fontSize: '11px', color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                          SEPA-Mahnung: {formatDate(item.sepa_mahnung_am)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Aktion */}
              <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button
                  onClick={handleBatchSend}
                  disabled={batchBusy || selectedTotal === 0}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: batchBusy || selectedTotal === 0 ? '#9CA3AF' : '#2563EB',
                    color: 'white', border: 'none', borderRadius: '6px',
                    cursor: batchBusy || selectedTotal === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '14px', fontWeight: '600',
                    display: 'flex', alignItems: 'center', gap: '6px'
                  }}
                >
                  {batchBusy
                    ? <Loader size={15} style={{ animation: 'spin 1s linear infinite' }} />
                    : <Mail size={15} />}
                  {batchBusy ? 'Versand laeuft ...' : `${selectedTotal} Mahnung(en) senden`}
                </button>
                <span style={{ fontSize: '12px', color: '#6B7280' }}>
                  {selectedL1.size} Zahlungserinnerung(en), {selectedL3.size} SEPA-Mahnung(en), {selectedL2.size} letzte Erinnerung(en)
                </span>
              </div>
            </>
          )}

          {/* Ergebnis Batch-Versand */}
          {batchResult && (
            <div style={{
              marginTop: '16px', padding: '12px 16px', borderRadius: '6px',
              backgroundColor: batchResult.failed === 0 ? '#ECFDF5' : '#FFFBEB',
              border: `1px solid ${batchResult.failed === 0 ? '#A7F3D0' : '#FCD34D'}`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: batchResult.errors.length > 0 ? '6px' : '0' }}>
                {batchResult.failed === 0
                  ? <Check size={18} color="#059669" />
                  : <AlertTriangle size={18} color="#D97706" />}
                <span style={{ fontSize: '14px', fontWeight: '600', color: batchResult.failed === 0 ? '#059669' : '#92400E' }}>
                  {batchResult.sent} versendet{batchResult.failed > 0 ? `, ${batchResult.failed} fehlgeschlagen` : ''}
                </span>
              </div>
              {batchResult.errors.length > 0 && (
                <ul style={{ margin: '4px 0 0', paddingLeft: '20px', fontSize: '13px', color: '#92400E' }}>
                  {batchResult.errors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {/* ====================================== */}
      {/* TAB-SWITCHER OFFEN/BEZAHLT             */}
      {/* ====================================== */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button
          onClick={() => setActiveTab('offen')}
          style={{
            padding: '6px 14px',
            backgroundColor: activeTab === 'offen' ? '#EFF6FF' : '#F9FAFB',
            color: activeTab === 'offen' ? '#2563EB' : '#6B7280',
            border: `1px solid ${activeTab === 'offen' ? '#BFDBFE' : '#E5E7EB'}`,
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: activeTab === 'offen' ? '600' : '400',
            display: 'flex', alignItems: 'center', gap: '6px'
          }}
        >
          Offen
          <span style={{
            backgroundColor: activeTab === 'offen' ? '#BFDBFE' : '#E5E7EB',
            padding: '1px 6px', borderRadius: '10px', fontSize: '11px', fontWeight: '600'
          }}>
            {openInvoices.length}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('bezahlt')}
          style={{
            padding: '6px 14px',
            backgroundColor: activeTab === 'bezahlt' ? '#EFF6FF' : '#F9FAFB',
            color: activeTab === 'bezahlt' ? '#2563EB' : '#6B7280',
            border: `1px solid ${activeTab === 'bezahlt' ? '#BFDBFE' : '#E5E7EB'}`,
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: activeTab === 'bezahlt' ? '600' : '400',
            display: 'flex', alignItems: 'center', gap: '6px'
          }}
        >
          Bezahlt
          <span style={{
            backgroundColor: activeTab === 'bezahlt' ? '#BFDBFE' : '#E5E7EB',
            padding: '1px 6px', borderRadius: '10px', fontSize: '11px', fontWeight: '600'
          }}>
            {paidInvoices.length}
          </span>
        </button>

        {activeTab === 'offen' && openInvoices.length > 0 && (
          <div style={{
            marginLeft: 'auto', alignSelf: 'center',
            fontSize: '13px', color: '#374151'
          }}>
            Summe offen: <strong>{formatCurrency(openSum)}</strong>
          </div>
        )}
      </div>

      {/* ====================================== */}
      {/* LISTE                                   */}
      {/* ====================================== */}
      {loadingLists ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
          Lade Rechnungen ...
        </div>
      ) : activeTab === 'offen' ? (
        // ===== OFFEN =====
        openInvoices.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '40px', color: '#6B7280',
            backgroundColor: '#F9FAFB', borderRadius: '8px'
          }}>
            <Check size={40} color="#A7F3D0" style={{ margin: '0 auto 12px' }} />
            <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '4px' }}>
              Keine offenen Rechnungen
            </div>
            <div style={{ fontSize: '14px' }}>
              Alle versendeten/freigegebenen Rechnungen sind bezahlt.
            </div>
          </div>
        ) : (
          <div style={{
            backgroundColor: 'white', borderRadius: '8px', border: '1px solid #E5E7EB',
            overflow: 'hidden'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '120px 110px 1fr 130px 160px 380px',
              gap: '12px', padding: '10px 16px',
              backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB',
              fontSize: '12px', fontWeight: '600', color: '#6B7280',
              textTransform: 'uppercase'
            }}>
              <div>Rechnungs-Nr.</div>
              <div>Datum</div>
              <div>Empfaenger</div>
              <div style={{ textAlign: 'right' }}>Brutto</div>
              <div style={{ textAlign: 'right' }}>Offen seit</div>
              <div style={{ textAlign: 'right' }}>Aktion</div>
            </div>
            {openInvoices.map(inv => {
              const days = daysSince(inv.rechnungsdatum);
              const isMarkRow = markPaidInvoiceId === inv.id;
              const isPaidBusy = markPaidBusy === inv.id;
              const isReminderBusy = reminderBusy === inv.id;
              const tageColor = days !== null && days > 30 ? '#DC2626' : days !== null && days > 14 ? '#D97706' : '#6B7280';

              // Sub-Zeile-Text und Button-Logik abhaengig von letzter Mahnstufe (Ablauf 1 -> 3 -> 2)
              const lvl = inv.last_reminder_level;
              let reminderInfoText: string;
              let reminderInfoColor: string;
              if (lvl === 2) {
                reminderInfoText = `Letzte Erinnerung: ${formatDate(inv.last_reminder_sent_at)}`;
                reminderInfoColor = '#DC2626';
              } else if (lvl === 3) {
                reminderInfoText = `SEPA-Mahnung: ${formatDate(inv.last_reminder_sent_at)}`;
                reminderInfoColor = '#D97706';
              } else if (lvl === 1) {
                reminderInfoText = `Erinnert: ${formatDate(inv.last_reminder_sent_at)}`;
                reminderInfoColor = '#9CA3AF';
              } else {
                reminderInfoText = 'Noch nicht erinnert';
                reminderInfoColor = '#9CA3AF';
              }

              return (
                <div key={inv.id} style={{
                  display: 'grid',
                  gridTemplateColumns: '120px 110px 1fr 130px 160px 380px',
                  gap: '12px', padding: '12px 16px',
                  borderBottom: '1px solid #F3F4F6',
                  fontSize: '14px', color: '#111827',
                  alignItems: 'center'
                }}>
                  <div style={{ fontWeight: '500' }}>{inv.rechnungsnummer}</div>
                  <div>{formatDate(inv.rechnungsdatum)}</div>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {inv.empfaenger_firma}
                  </div>
                  <div style={{ textAlign: 'right', fontWeight: '500' }}>{formatCurrency(inv.brutto_betrag)}</div>
                  <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                    <div style={{ color: tageColor, fontWeight: days !== null && days > 14 ? '500' : '400' }}>
                      {days !== null ? `${days} Tage` : '–'}
                    </div>
                    <div style={{ fontSize: '11px', color: reminderInfoColor, whiteSpace: 'nowrap', fontWeight: (lvl === 2 || lvl === 3) ? '500' : '400' }}>
                      {reminderInfoText}
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px', alignItems: 'center' }}>
                    {isMarkRow ? (
                      <>
                        <Calendar size={14} color="#6B7280" />
                        <input
                          type="date"
                          value={markPaidDatum}
                          onChange={(e) => setMarkPaidDatum(e.target.value)}
                          disabled={isPaidBusy}
                          style={{
                            padding: '4px 6px', border: '1px solid #D1D5DB',
                            borderRadius: '4px', fontSize: '13px'
                          }}
                        />
                        <button
                          onClick={() => confirmMarkPaid(inv)}
                          disabled={isPaidBusy || !markPaidDatum}
                          style={{
                            padding: '4px 10px', backgroundColor: isPaidBusy ? '#9CA3AF' : '#10B981',
                            color: 'white', border: 'none', borderRadius: '4px',
                            cursor: isPaidBusy ? 'not-allowed' : 'pointer',
                            fontSize: '13px', fontWeight: '500',
                            display: 'flex', alignItems: 'center', gap: '4px'
                          }}
                        >
                          <Check size={12} /> {isPaidBusy ? '...' : 'OK'}
                        </button>
                        <button
                          onClick={cancelMarkPaid}
                          disabled={isPaidBusy}
                          style={{
                            padding: '4px 8px', backgroundColor: '#F3F4F6', color: '#374151',
                            border: '1px solid #D1D5DB', borderRadius: '4px',
                            cursor: isPaidBusy ? 'not-allowed' : 'pointer', fontSize: '13px'
                          }}
                        >
                          <X size={12} />
                        </button>
                      </>
                    ) : (
                      <>
                        {lvl === null && (
                          <button
                            onClick={() => handleSendReminder(inv, 1)}
                            disabled={isReminderBusy}
                            style={{
                              padding: '6px 12px', backgroundColor: '#EFF6FF', color: '#2563EB',
                              border: '1px solid #BFDBFE', borderRadius: '6px',
                              cursor: isReminderBusy ? 'not-allowed' : 'pointer',
                              fontSize: '13px', fontWeight: '500',
                              display: 'flex', alignItems: 'center', gap: '4px',
                              opacity: isReminderBusy ? 0.6 : 1
                            }}
                          >
                            <Mail size={14} /> {isReminderBusy ? 'Wird versendet...' : 'Erinnerung senden'}
                          </button>
                        )}
                        {lvl === 1 && inv.zahlungsart !== 'lastschrift' && (
                          <button
                            onClick={() => handleSendReminder(inv, 3)}
                            disabled={isReminderBusy}
                            style={{
                              padding: '6px 12px', backgroundColor: '#EEF2FF', color: '#4338CA',
                              border: '1px solid #C7D2FE', borderRadius: '6px',
                              cursor: isReminderBusy ? 'not-allowed' : 'pointer',
                              fontSize: '13px', fontWeight: '500',
                              display: 'flex', alignItems: 'center', gap: '4px',
                              opacity: isReminderBusy ? 0.6 : 1
                            }}
                          >
                            <CreditCard size={14} /> {isReminderBusy ? 'Wird versendet...' : 'SEPA-Mahnung senden'}
                          </button>
                        )}
                        {lvl === 3 && (
                          <button
                            onClick={() => handleSendReminder(inv, 2)}
                            disabled={isReminderBusy}
                            style={{
                              padding: '6px 12px', backgroundColor: '#FEF3C7', color: '#92400E',
                              border: '1px solid #FCD34D', borderRadius: '6px',
                              cursor: isReminderBusy ? 'not-allowed' : 'pointer',
                              fontSize: '13px', fontWeight: '500',
                              display: 'flex', alignItems: 'center', gap: '4px',
                              opacity: isReminderBusy ? 0.6 : 1
                            }}
                          >
                            <AlertCircle size={14} /> {isReminderBusy ? 'Wird versendet...' : 'Letzte Erinnerung senden'}
                          </button>
                        )}
                        <button
                          onClick={() => startMarkPaid(inv.id)}
                          style={{
                            padding: '6px 12px', backgroundColor: '#ECFDF5', color: '#059669',
                            border: '1px solid #A7F3D0', borderRadius: '6px', cursor: 'pointer',
                            fontSize: '13px', fontWeight: '500',
                            display: 'flex', alignItems: 'center', gap: '4px'
                          }}
                        >
                          <Check size={14} /> Als bezahlt markieren
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        // ===== BEZAHLT =====
        paidInvoices.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '40px', color: '#6B7280',
            backgroundColor: '#F9FAFB', borderRadius: '8px'
          }}>
            <AlertTriangle size={40} color="#D1D5DB" style={{ margin: '0 auto 12px' }} />
            <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '4px' }}>
              Noch keine bezahlten Rechnungen
            </div>
            <div style={{ fontSize: '14px' }}>
              Hier erscheinen Rechnungen, sobald ein Zahlungseingang automatisch oder manuell zugeordnet wurde.
            </div>
          </div>
        ) : (
          <div style={{
            backgroundColor: 'white', borderRadius: '8px', border: '1px solid #E5E7EB',
            overflow: 'hidden'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '120px 110px 1fr 120px 110px 130px 200px',
              gap: '12px', padding: '10px 16px',
              backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB',
              fontSize: '12px', fontWeight: '600', color: '#6B7280',
              textTransform: 'uppercase'
            }}>
              <div>Rechnungs-Nr.</div>
              <div>Datum</div>
              <div>Empfaenger</div>
              <div style={{ textAlign: 'right' }}>Brutto</div>
              <div>Bezahlt am</div>
              <div>Quelle</div>
              <div style={{ textAlign: 'right' }}>Aktion</div>
            </div>
            {paidInvoices.map(inv => {
              const isBusy = markUnpaidBusy === inv.id;
              const isBankMatch = inv.bezahlt_bank_ref !== null;
              return (
                <div key={inv.id} style={{
                  display: 'grid',
                  gridTemplateColumns: '120px 110px 1fr 120px 110px 130px 200px',
                  gap: '12px', padding: '12px 16px',
                  borderBottom: '1px solid #F3F4F6',
                  fontSize: '14px', color: '#111827',
                  alignItems: 'center'
                }}>
                  <div style={{ fontWeight: '500' }}>{inv.rechnungsnummer}</div>
                  <div>{formatDate(inv.rechnungsdatum)}</div>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {inv.empfaenger_firma}
                  </div>
                  <div style={{ textAlign: 'right', fontWeight: '500' }}>{formatCurrency(inv.brutto_betrag)}</div>
                  <div>{formatDate(inv.bezahlt_am)}</div>
                  <div>
                    {isBankMatch ? (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        padding: '2px 8px', borderRadius: '10px',
                        backgroundColor: '#EFF6FF', color: '#2563EB',
                        border: '1px solid #BFDBFE',
                        fontSize: '11px', fontWeight: '500'
                      }}>
                        <Banknote size={11} /> Bank-Match
                      </span>
                    ) : (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        padding: '2px 8px', borderRadius: '10px',
                        backgroundColor: '#F3F4F6', color: '#6B7280',
                        border: '1px solid #E5E7EB',
                        fontSize: '11px', fontWeight: '500'
                      }}>
                        <Hand size={11} /> Manuell
                      </span>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <button
                      onClick={() => handleMarkUnpaid(inv)}
                      disabled={isBusy}
                      style={{
                        padding: '6px 12px', backgroundColor: '#FEF2F2', color: '#DC2626',
                        border: '1px solid #FECACA', borderRadius: '6px',
                        cursor: isBusy ? 'not-allowed' : 'pointer',
                        fontSize: '13px', fontWeight: '500',
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        opacity: isBusy ? 0.6 : 1
                      }}
                    >
                      <RotateCcw size={14} /> {isBusy ? 'Wird zurueckgesetzt...' : 'Als unbezahlt markieren'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
};
